// Light/dark theme management. Persists the user choice and applies the
// `dark` class on <html> so Tailwind's `dark:` variants and our CSS tokens react.
// NOTE: only a non-sensitive UI preference ("light"/"dark") is stored in
// localStorage — no personal or authentication data, so plain storage is safe.
import { isNight } from "@/lib/daylight";

const KEY = "vsp_theme";

// Fallback location (metropolitan France centre) used to resolve the automatic
// night mode before/without a precise geolocation fix.
const FALLBACK_LAT = 46.6;
const FALLBACK_LON = 2.4;

export function getStoredTheme() {
  try {
    return localStorage.getItem(KEY);
  } catch (err) {
    console.warn("Lecture thème:", err);
    return null;
  }
}

export function systemPrefersDark() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

// "auto" = no explicit light/dark choice stored -> the app follows the sun.
export function isAutoMode() {
  const stored = getStoredTheme();
  return stored !== "light" && stored !== "dark";
}

// Resolve the theme for the automatic mode from the sun position.
export function resolveAutoTheme(lat = FALLBACK_LAT, lon = FALLBACK_LON, date = new Date()) {
  return isNight(lat, lon, date) ? "dark" : "light";
}

export function getActiveTheme() {
  const stored = getStoredTheme();
  if (stored === "light" || stored === "dark") return stored;
  // Automatic: dark after sunset / before sunrise (estimated at the fallback
  // location until AutoNightTheme refines it with the real position).
  return resolveAutoTheme();
}

export function applyTheme(theme) {
  const root = document.documentElement;
  if (theme === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
}

// Apply the sun-based theme, but ONLY while the user hasn't made an explicit
// light/dark choice (manual choice always wins).
export function applyAutoTheme(lat, lon) {
  if (!isAutoMode()) return;
  applyTheme(resolveAutoTheme(lat, lon));
}

export function setTheme(theme) {
  try {
    localStorage.setItem(KEY, theme);
  } catch (err) {
    console.warn("Enregistrement thème:", err);
  }
  applyTheme(theme);
}

// Apply the resolved theme as early as possible.
export function initTheme() {
  applyTheme(getActiveTheme());
}
